import { TextField, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { getFeesForCurrentPermit } from 'src/pages/Permits/common/getFeesForCurrentPermit';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { httpRequest } from 'src/services/httpRequest';
import { getLateFeeAmount, isPetLateFeesWaived } from 'src/components/common/calculatePetFee';

const PET_PERMIT_ID = '10';

function index ({
  setTaxRate,
  setFeeDetails,
  feeDetails,
  isSpayedNeutered = undefined,
  petFeeFormContext
}: {
  setTaxRate: (rate: number) => void;
  setFeeDetails: (details: any) => void;
  feeDetails: any;
  isSpayedNeutered?: unknown;
  petFeeFormContext?: {
    applicationDate?: string | Date | null;
    lateFeesWaived?: unknown;
  };
}) {
  const dispatch = useDispatch();
  const { permit } = useParams();
  const [loading, setLoading] = useState<boolean>(false);

  const handleGetApplicationFee = async () => {
    setLoading(true);
    const response = await httpRequest(
      'GET',
      `/configuration/fee_details?PermitID=${permit}`
    );
    if (response?.status === 'success') {
      const feeObject = getFeesForCurrentPermit(response.data);
      const applicationFeeSpayed = Number(feeObject?.ApplicationFeeSpayed) || 0;
      const applicationNonFeeSpayed =
        Number(feeObject?.ApplicationFeeNonSpayed) || 0;
      const baseApplicationFee = Number(feeObject?.ApplicationFee) || 0;

      setTaxRate(feeObject?.CreditCardFee || 0);
      setFeeDetails({
        applicationFee: baseApplicationFee,
        inspectionFee: Number(feeObject?.InspectionFee) || 0,
        applicationFeeSpayed,
        applicationNonFeeSpayed,
        pilotClinicFeePet: Number(feeObject?.PilotClinicFeePet) || 0,
        stateRegistrationFeePet: Number(feeObject?.StateRegistrationFeePet) || 0,
        lateMonthlyFee: Number(feeObject?.LateMonthlyFee) || 0,
        delinquencyFee: Number(feeObject?.DelinquencyFee) || 0,
      });
      setLoading(false);
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  useEffect(() => {
    handleGetApplicationFee();
  }, []);

  const isSpayed =
    isSpayedNeutered === true ||
    isSpayedNeutered === 1 ||
    isSpayedNeutered === '1' ||
    isSpayedNeutered === 'Yes' ||
    isSpayedNeutered === 'yes';

  useEffect(() => {
    if (
      permit === PET_PERMIT_ID &&
      feeDetails?.applicationFeeSpayed !== undefined &&
      feeDetails?.applicationNonFeeSpayed !== undefined
    ) {
      const baseFee = isSpayed
        ? feeDetails.applicationFeeSpayed
        : feeDetails.applicationNonFeeSpayed;
      if (feeDetails.applicationFee !== baseFee) {
        setFeeDetails({ ...feeDetails, applicationFee: baseFee });
      }
    }
  }, [
    permit,
    isSpayed,
    feeDetails?.applicationFee,
    feeDetails?.applicationFeeSpayed,
    feeDetails?.applicationNonFeeSpayed,
  ]);

  const hasPetSelected = isSpayedNeutered !== undefined && isSpayedNeutered !== null;
  const displayFee =
    permit === PET_PERMIT_ID && !hasPetSelected
      ? 0
      : permit === PET_PERMIT_ID &&
        (feeDetails?.applicationFeeSpayed > 0 || feeDetails?.applicationNonFeeSpayed > 0)
        ? isSpayed
          ? feeDetails.applicationFeeSpayed
          : feeDetails.applicationNonFeeSpayed
        : feeDetails?.applicationFee || 0;

  const showPetLateLines =
    permit === PET_PERMIT_ID &&
    hasPetSelected &&
    petFeeFormContext &&
    displayFee > 0;

  let petLateFeeAmount = 0;
  if (showPetLateLines) {
    const raw = petFeeFormContext.applicationDate;
    const parsed =
      raw != null && raw !== ''
        ? new Date(raw as string | Date)
        : new Date();
    const appDate = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    petLateFeeAmount = isPetLateFeesWaived(petFeeFormContext.lateFeesWaived)
      ? 0
      : getLateFeeAmount(
          displayFee,
          feeDetails?.lateMonthlyFee || 0,
          feeDetails?.delinquencyFee || 0,
          appDate,
          isSpayed
        );
  }
  const petTotalWithLate = showPetLateLines ? displayFee + petLateFeeAmount : displayFee;

  /** Pet license: three outlined fields in a row (Application / Late / Total), matching New Application layout */
  const showPetFeeSummaryRow =
    permit === PET_PERMIT_ID &&
    hasPetSelected &&
    Boolean(petFeeFormContext) &&
    displayFee > 0;

  /** Even-width row on tablet+ like the pet license fee mockup */
  const outlinedReadOnlySx = {
    flex: { xs: 'unset', sm: '1 1 0' },
    minWidth: { xs: '100%', sm: 0 },
    width: { xs: '100%' }
  } as const;

  return (
    <>
      {loading ? (
        <LoadingOverlay />
      ) : (
        <Box width="100%">
          {showPetFeeSummaryRow ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                gap: 2,
                width: '100%',
                mt: 2,
                alignItems: { xs: 'stretch', sm: 'flex-start' }
              }}
            >
              <TextField
                disabled
                sx={outlinedReadOnlySx}
                value={`$${Number(displayFee).toFixed(2)}`}
                label="Application Fee"
                variant="outlined"
                InputLabelProps={{ className: 'weight-600' }}
              />
              <TextField
                disabled
                sx={outlinedReadOnlySx}
                value={`$${petLateFeeAmount.toFixed(2)}`}
                label={
                  isPetLateFeesWaived(petFeeFormContext?.lateFeesWaived)
                    ? 'Late Fee (waived)'
                    : 'Late Fee'
                }
                variant="outlined"
                InputLabelProps={{ className: 'weight-600' }}
              />
              <TextField
                disabled
                sx={outlinedReadOnlySx}
                value={`$${petTotalWithLate.toFixed(2)}`}
                label="Total Application"
                variant="outlined"
                InputLabelProps={{ className: 'weight-600' }}
              />
            </Box>
          ) : (
            <>
              {(displayFee > 0 || feeDetails?.applicationFee > 0) && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    id="outlined-basic"
                    disabled
                    fullWidth={permit === PET_PERMIT_ID}
                    sx={permit === PET_PERMIT_ID ? { maxWidth: { sm: 280 } } : undefined}
                    value={`$${Number(displayFee).toFixed(2)}`}
                    label="Application Fee"
                    variant="outlined"
                    InputLabelProps={{
                      className: 'weight-600'
                    }}
                  />
                </Box>
              )}
            </>
          )}

          {feeDetails?.inspectionFee > 0 && (
            <TextField
              id="outlined-basic-inspection"
              disabled
              sx={{ mt: 2 }}
              value={`$ ${feeDetails?.inspectionFee}.00` || 0}
              label="Inspection Fee"
              variant="outlined"
              InputLabelProps={{
                className: 'weight-600'
              }}
            />
          )}
        </Box>
      )}
    </>
  );
}

export default index;
